/**
 * threadMention — the client contract for the mention inbox (2026-09-17).
 *
 * Verbatim twin of the row `functions/src/notifications/mentionInbox.ts` writes
 * to `users/{uid}/thread_mentions/{anchorId}`. Pure and Firebase-free on
 * purpose: the mentions list, the unread badge and the tab strip all read these
 * predicates, and a module that reaches `firebase/auth` at import time takes
 * down every suite that renders a component importing it (the
 * `attachmentStorage` lesson, CLAUDE.md).
 *
 * READ STATE DOES NOT LIVE ON THE ROW. The rows are server-write-only — they
 * record what somebody else said, so a client able to edit them could invent or
 * erase a mention — and what this person has read is their own fact, kept on
 * `users/{uid}/chat_threads_meta/mentions` as a high-water mark plus a capped
 * map of individually-read ids. That is the same shape the group-thread read
 * receipts already use (`lastReadOrderTs` + `seen`), and it means "mark all
 * read" is ONE write no matter how many mentions exist.
 */

/** Why a row is in the list. An explicit `@` outranks a reply. */
export type MentionReason = 'mention' | 'reply';

/** One row of users/{uid}/thread_mentions. */
export interface ThreadMention {
  /** The group-store document id — also the row id, and what the transcript wears. */
  id: string;
  threadId: string;
  accountId: string;
  orderTs: number;
  /** Blank when Prest0n wrote it (he is not a participant record). */
  senderUid: string;
  senderName: string;
  byPrest0n: boolean;
  reason: MentionReason;
  snippet: string;
  threadTitle: string;
}

/** users/{uid}/chat_threads_meta/mentions — the viewer's own read state. */
export interface MentionReadState {
  /** Everything at or below this instant counts as read. */
  lastReadAtMs: number;
  /** Individually-read ids above that mark. */
  read: Readonly<Record<string, true>>;
}

export const EMPTY_MENTION_READ_STATE: MentionReadState = { lastReadAtMs: 0, read: {} };

/**
 * Most individually-read ids kept. Beyond this the oldest are dropped — they
 * are already below a moving high-water mark in every ordinary case, and an
 * unbounded map on a document the client rewrites is how a doc grows past
 * Firestore's 1 MiB limit and starts refusing every later write.
 */
export const MENTION_READ_MAP_CAP = 200;

/** Newest first, the order the list renders in. Stable for equal instants. */
export function compareMentionsNewestFirst(a: ThreadMention, b: ThreadMention): number {
  if (b.orderTs !== a.orderTs) return b.orderTs - a.orderTs;
  return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
}

/**
 * Has this person read this mention? A row at or below the high-water mark is
 * read whether or not it is named in the map — that is what makes "mark all
 * read" one write rather than N.
 */
export function isMentionUnread(row: ThreadMention, state: MentionReadState | null | undefined): boolean {
  if (!state) return true;
  if (row.orderTs <= state.lastReadAtMs) return false;
  return state.read[row.id] !== true;
}

export function countUnreadMentions(
  rows: readonly ThreadMention[],
  state: MentionReadState | null | undefined,
): number {
  let n = 0;
  for (const row of rows) if (isMentionUnread(row, state)) n += 1;
  return n;
}

/**
 * One row read out of a Firestore document. Tolerant by design: a row whose
 * shape a later version changed should degrade to something renderable, never
 * throw inside a snapshot handler and take the whole listener down.
 */
export function parseThreadMention(id: string, data: Record<string, unknown> | undefined): ThreadMention | null {
  const rowId = String(id || '').trim();
  const threadId = String(data?.threadId || '').trim();
  if (!rowId || !threadId) return null;
  const orderTs = Number(data?.orderTs);
  return {
    id: rowId,
    threadId,
    accountId: String(data?.accountId || '').trim(),
    orderTs: Number.isFinite(orderTs) ? orderTs : 0,
    senderUid: String(data?.senderUid || '').trim(),
    senderName: String(data?.senderName || '').trim(),
    byPrest0n: data?.byPrest0n === true,
    reason: data?.reason === 'reply' ? 'reply' : 'mention',
    snippet: String(data?.snippet || ''),
    threadTitle: String(data?.threadTitle || '').trim(),
  };
}

/** The stored read state, read tolerantly. Absent ⇒ nothing is read yet. */
export function parseMentionReadState(data: Record<string, unknown> | undefined): MentionReadState {
  const mark = Number(data?.lastReadAtMs);
  const raw = data?.read;
  const read: Record<string, true> = {};
  if (raw && typeof raw === 'object') {
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
      if (value === true && key) read[key] = true;
    }
  }
  return { lastReadAtMs: Number.isFinite(mark) && mark > 0 ? mark : 0, read };
}

/**
 * The read state after one mention is opened. Pure, so the eviction rule is
 * assertable without a Firestore.
 *
 * Entries at or below the high-water mark are dropped first — they are already
 * read by the mark alone, so keeping them is pure weight. Only if the map is
 * still over the cap are the oldest-by-insertion entries dropped, which is safe
 * in the only direction that matters: an evicted id above the mark reads as
 * unread again, and showing someone a mention twice is a far smaller failure
 * than hiding one they never saw.
 */
export function markMentionRead(
  state: MentionReadState,
  row: Pick<ThreadMention, 'id' | 'orderTs'>,
  cap: number = MENTION_READ_MAP_CAP,
): MentionReadState {
  if (row.orderTs <= state.lastReadAtMs) return state;
  if (state.read[row.id] === true) return state;
  const next: Record<string, true> = { ...state.read, [row.id]: true };
  const keys = Object.keys(next);
  if (keys.length <= cap) return { lastReadAtMs: state.lastReadAtMs, read: next };
  for (const key of keys) {
    if (Object.keys(next).length <= cap) break;
    if (key !== row.id) delete next[key];
  }
  return { lastReadAtMs: state.lastReadAtMs, read: next };
}

/**
 * Everything up to `throughMs` is read, and the map is cleared because every id
 * it held is now below the mark. This is the whole of "mark all read": one
 * value, one write, regardless of how many rows exist or how many the browser
 * happens to be holding.
 */
export function markAllMentionsRead(throughMs: number): MentionReadState {
  return { lastReadAtMs: Math.max(0, Math.floor(throughMs)), read: {} };
}
